// stores/registration.js
import { defineStore } from 'pinia';

const REG_API = process.env.VUE_APP_REGISTRATION_API_URL || '/api/registration';

export const useRegistrationStore = defineStore('registration', {
  state: () => {
    // Load persisted tokens immediately on store init — fixes 401 on page reload
    let tokens = null;
    let user = null;
    try {
      const t = sessionStorage.getItem('reg_tokens');
      const u = sessionStorage.getItem('reg_user');
      if (t) tokens = JSON.parse(t);
      if (u) user = JSON.parse(u);
    } catch (_) { /* ignored */ }
    return {
      user,
      tokens,
      structures: [],
      currentStructure: null,
      isLoading: false,
      error: null,
    };
  },

  getters: {
    isLoggedIn: (state) => !!state.tokens?.access && !!state.user,
    authHeaders: (state) => state.tokens?.access
      ? { Authorization: `Bearer ${state.tokens.access}` }
      : {},
  },

  actions: {
    _persist() {
      try {
        sessionStorage.setItem('reg_tokens', JSON.stringify(this.tokens));
        sessionStorage.setItem('reg_user', JSON.stringify(this.user));
      } catch (_) { /* ignored */ }
    },

    // Central fetch wrapper — auto-logout on expired/invalid token
    async _apiFetch(url, options = {}) {
      const res = await fetch(url, options);
      if (res.status === 401) {
        const body = await res.clone().json().catch(() => ({}));
        const detail = (body?.detail || '').toLowerCase();
        if (detail.includes('invalid') || detail.includes('expired') || detail.includes('token')) {
          this.logout();
          window.location.href = '/register/login?reason=session_expired';
          throw new Error('SESSION_EXPIRED');
        }
      }
      return res;
    },

    loadFromSession() {
      try {
        const t = sessionStorage.getItem('reg_tokens');
        const u = sessionStorage.getItem('reg_user');
        if (t) this.tokens = JSON.parse(t);
        if (u) this.user = JSON.parse(u);
      } catch (_) { /* ignored */ }
    },

    logout() {
      this.tokens = null;
      this.user = null;
      this.structures = [];
      this.currentStructure = null;
      sessionStorage.removeItem('reg_tokens');
      sessionStorage.removeItem('reg_user');
    },

    async register(payload) {
      this.isLoading = true;
      this.error = null;
      try {
        const res = await this._apiFetch(`${REG_API}/register/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(Object.values(data).flat().join(' ') || 'Registration failed');
        return data;
      } catch (e) {
        this.error = e.message;
        throw e;
      } finally {
        this.isLoading = false;
      }
    },

    async loginStep1(email, password) {
      // Step 1: validate credentials → server sends OTP via email + WhatsApp
      this.isLoading = true;
      this.error = null;
      try {
        const res = await this._apiFetch(`${REG_API}/login/step1/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Login failed');
        return true;
      } catch (e) {
        this.error = e.message;
        throw e;
      } finally {
        this.isLoading = false;
      }
    },

    async loginStep2(email, otp) {
      // Step 2: validate OTP → receive tokens
      this.isLoading = true;
      this.error = null;
      try {
        const res = await this._apiFetch(`${REG_API}/login/step2/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, otp_code: otp }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Invalid code');
        this.tokens = data.tokens;
        this.user = data.user;
        this._persist();
        return true;
      } catch (e) {
        this.error = e.message;
        throw e;
      } finally {
        this.isLoading = false;
      }
    },

    // Keep legacy login for backward compat (not used by UI anymore)
    async login(email, password) {
      return this.loginStep1(email, password);
    },

    async fetchStructures() {
      this.isLoading = true;
      try {
        const res = await this._apiFetch(`${REG_API}/structures/`, {
          headers: this.authHeaders,
        });
        if (!res.ok) return;
        this.structures = await res.json();
      } catch (e) {
        if (e.message !== 'SESSION_EXPIRED') this.error = e.message;
      } finally {
        this.isLoading = false;
      }
    },

    async saveStructure(id, formData) {
      const url = id ? `${REG_API}/structures/${id}/` : `${REG_API}/structures/`;
      const method = id ? 'PATCH' : 'POST';
      const isFormData = formData instanceof FormData;
      const res = await this._apiFetch(url, {
        method,
        headers: isFormData ? this.authHeaders : { ...this.authHeaders, 'Content-Type': 'application/json' },
        body: isFormData ? formData : JSON.stringify(formData),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(JSON.stringify(json));
      if (!id) this.structures.push(json);
      else {
        const idx = this.structures.findIndex(s => s.id === json.id);
        if (idx >= 0) this.structures[idx] = json;
      }
      return json;
    },

    async submitStructure(id) {
      const res = await this._apiFetch(`${REG_API}/structures/${id}/submit/`, {
        method: 'POST',
        headers: this.authHeaders,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Submit failed');
      const idx = this.structures.findIndex(s => s.id === id);
      if (idx >= 0) this.structures[idx] = json;
      return json;
    },

    async uploadSigned(id, file) {
      const fd = new FormData();
      fd.append('signed_document', file);
      const res = await this._apiFetch(`${REG_API}/structures/${id}/upload-signed/`, {
        method: 'POST',
        headers: this.authHeaders,
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Upload failed');
      return json;
    },

    getDownloadUrl(id, format) {
      return `${REG_API}/structures/${id}/download-${format}/`;
    },

    async addService(structureId, data) {
      const res = await this._apiFetch(`${REG_API}/structures/${structureId}/services/`, {
        method: 'POST',
        headers: { ...this.authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(JSON.stringify(json));
      return json;
    },

    async updateService(structureId, serviceId, data) {
      const res = await this._apiFetch(`${REG_API}/structures/${structureId}/services/${serviceId}/`, {
        method: 'PUT',
        headers: { ...this.authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(JSON.stringify(json));
      return json;
    },

    async deleteService(structureId, serviceId) {
      await this._apiFetch(`${REG_API}/structures/${structureId}/services/${serviceId}/`, {
        method: 'DELETE', headers: this.authHeaders,
      });
    },

    async addPersonnel(structureId, data) {
      // data may be FormData (has photo) or plain object
      const isFormData = data instanceof FormData;
      const res = await this._apiFetch(`${REG_API}/structures/${structureId}/personnel/`, {
        method: 'POST',
        headers: isFormData ? this.authHeaders : { ...this.authHeaders, 'Content-Type': 'application/json' },
        body: isFormData ? data : JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(JSON.stringify(json));
      return json;
    },

    async updatePersonnel(structureId, personnelId, data) {
      const isFormData = data instanceof FormData;
      const res = await this._apiFetch(`${REG_API}/structures/${structureId}/personnel/${personnelId}/`, {
        method: 'PUT',
        headers: isFormData ? this.authHeaders : { ...this.authHeaders, 'Content-Type': 'application/json' },
        body: isFormData ? data : JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(JSON.stringify(json));
      return json;
    },

    async deletePersonnel(structureId, personnelId) {
      await this._apiFetch(`${REG_API}/structures/${structureId}/personnel/${personnelId}/`, {
        method: 'DELETE', headers: this.authHeaders,
      });
    },

    async addDelegation(structureId, data) {
      const res = await this._apiFetch(`${REG_API}/structures/${structureId}/delegations/`, {
        method: 'POST',
        headers: { ...this.authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(JSON.stringify(json));
      return json;
    },

    async updateDelegation(structureId, delegationId, data) {
      const res = await this._apiFetch(`${REG_API}/structures/${structureId}/delegations/${delegationId}/`, {
        method: 'PUT',
        headers: { ...this.authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(JSON.stringify(json));
      return json;
    },

    async deleteDelegation(structureId, delegationId) {
      await this._apiFetch(`${REG_API}/structures/${structureId}/delegations/${delegationId}/`, {
        method: 'DELETE', headers: this.authHeaders,
      });
    },
  },
});